// Settings → Messages: the "candidate messages after application" pause.
//
// Paused by default for every school (server: messagingHold.service.js). Only
// a recruitment admin can change it — the server refuses anyone else, and
// this screen shows that refusal rather than guessing permissions. Every
// change needs a reason and is kept in the history below, newest first.
//
// Lifting the pause goes through a confirm step that names exactly what will
// start sending, because that is the moment candidates start hearing from us.
import React, { useState } from 'react';
import { PlayCircle, PauseCircle } from 'lucide-react';
import api from '../../services/api';
import useMessagingHold from '../../hooks/useMessagingHold';
import MessagingHoldBanner from '../../components/common/messagingHold/MessagingHoldBanner';

const fmt = (d) => {
  try { return new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const WHAT_STARTS = [
  'Shortlisting messages',
  'Test, assessment and questionnaire links and invitations',
  'Interview invitations, reminders and reschedule confirmations',
  'Not-moving-forward messages',
  'Offers and offer reminders',
];

export default function MessagingHoldSwitch() {
  const hold = useMessagingHold();
  const [confirming, setConfirming] = useState(null); // null | 'lift' | 'pause'
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const submit = async () => {
    if (!reason.trim()) { setNote('Give a reason — it is kept with the change.'); return; }
    setBusy(true);
    setNote('');
    try {
      await api.put('/school-settings/messaging-hold', { paused: confirming === 'pause', reason: reason.trim() });
      const wasPausing = confirming === 'pause';
      setConfirming(null);
      setReason('');
      setNote(wasPausing ? 'Candidate messages are paused again.' : 'Candidate messages after application are now sending.');
      await hold.reload();
    } catch (e) {
      setNote(e?.response?.data?.message || 'Could not change it — try again.');
    } finally {
      setBusy(false);
    }
  };

  if (hold.loading) return <div className="text-sm text-gray-500">Checking whether candidate messages are paused…</div>;

  const startsList = hold.heldWhilePaused.length ? hold.heldWhilePaused : WHAT_STARTS;

  return (
    <div className="space-y-3">
      <MessagingHoldBanner paused={hold.paused} showLink={false} />

      <div className="rounded-lg border border-gray-200 px-4 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Candidate messages after application</h2>
            <p className="mt-1 text-sm text-gray-600">
              {hold.paused
                ? 'Paused. Only application confirmations and fee reminders go to candidates.'
                : 'Sending. Candidates receive messages at every step of the pipeline.'}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Only a recruitment admin can change this. Every change is recorded with who made it and why.
            </p>
          </div>
          {!confirming && (
            hold.paused ? (
              <button
                onClick={() => { setConfirming('lift'); setNote(''); }}
                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-green-700 px-3 py-2 text-sm font-medium text-white hover:bg-green-800"
              >
                <PlayCircle className="h-4 w-4" /> Start sending…
              </button>
            ) : (
              <button
                onClick={() => { setConfirming('pause'); setNote(''); }}
                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-black"
              >
                <PauseCircle className="h-4 w-4" /> Pause candidate messages…
              </button>
            )
          )}
        </div>

        {confirming === 'lift' && (
          <div className="mt-4 rounded-lg border border-orange-300 bg-orange-50 p-4 text-sm">
            <p className="font-semibold text-gray-900">
              Lifting the pause starts these going to every candidate of this school, from the next step each one reaches:
            </p>
            <ul className="mt-2 list-disc space-y-0.5 pl-5 text-gray-800">
              {startsList.map((x) => <li key={x}>{x}</li>)}
            </ul>
            <p className="mt-2 text-gray-700">
              Candidate stage links that were closed will open, including any already copied or sent.
            </p>
          </div>
        )}
        {confirming === 'pause' && (
          <div className="mt-4 rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm text-gray-800">
            Pausing stops everything after the application confirmations and fee reminders, and closes candidate
            stage links for everyone except test candidates.
          </div>
        )}

        {confirming && (
          <div className="mt-3 space-y-2">
            <label className="block text-xs font-medium text-gray-700" htmlFor="hold-reason">Reason (kept with the change)</label>
            <input
              id="hold-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder={confirming === 'lift' ? 'e.g. Trial job checked; approved to start' : 'e.g. Wording under review'}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
            />
            <div className="flex gap-2">
              <button
                disabled={busy}
                onClick={submit}
                className={`rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-60 ${confirming === 'lift' ? 'bg-green-700 hover:bg-green-800' : 'bg-gray-900 hover:bg-black'}`}
              >
                {confirming === 'lift' ? 'Yes, start sending to candidates' : 'Yes, pause candidate messages'}
              </button>
              <button
                disabled={busy}
                onClick={() => { setConfirming(null); setReason(''); setNote(''); }}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {note && <p className="mt-3 text-sm text-gray-800">{note}</p>}

        {hold.history.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">History</h3>
            <ul className="mt-2 space-y-1.5 text-sm">
              {hold.history.map((h, i) => (
                <li key={`${h.at}-${i}`} className="text-gray-700">
                  <span className="font-medium">{h.paused ? 'Paused' : 'Started sending'}</span>
                  {' '}by {h.byName || 'someone'} · {fmt(h.at)}
                  {h.reason ? <span className="text-gray-500"> — “{h.reason}”</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
